import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import WindowControls from '../Common/WindowControls';
import EnrollmentSteps from './EnrollmentSteps';
import VoiceRecorder from './VoiceRecorder';
import VoiceVerification from './VoiceVerification';
import ExistingUserPrompt from './ExistingUserPrompt';
import { voiceSkipService } from '../../services/voiceSkipService';
import { Mic, Shield, ChevronRight, Clock, AlertTriangle } from 'lucide-react';

const EnrollmentContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.dark.bg};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const TitleBar = styled.div`
  height: 32px;
  background: ${({ theme }) => theme.colors.dark.surface};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
  -webkit-app-region: drag;
  position: relative;
  z-index: 100;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  -webkit-app-region: no-drag;
`;

const AppTitle = styled.h1`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const StatusIndicator = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.idle};
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;
  min-height: 0;
`;

const TimelineContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  
  @media (max-width: 768px) {
    justify-content: center;
    padding-right: 0;
    padding-top: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.xl};
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 600px;
`;

const StepContainer = styled(motion.div)`
  width: 100%;
`;

const IntroSection = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const IntroIcon = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[500]}20, 
    ${({ theme }) => theme.colors.accent.purple}20
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    padding: 2px;
    background: linear-gradient(135deg, 
      ${({ theme }) => theme.colors.primary[500]}, 
      ${({ theme }) => theme.colors.accent.purple}
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, 
                  linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
  
  svg {
    width: 48px;
    height: 48px;
    color: ${({ theme }) => theme.colors.primary[500]};
  }
`;

const IntroTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const IntroDescription = styled.p`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
`;

const StartButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[500]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin: ${({ theme }) => theme.spacing.xl} auto 0;
  transition: all ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px ${({ theme }) => theme.colors.primary[500]}40;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SkipSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`;

const SkipText = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SkipButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.dark.text};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      color: ${({ theme }) => theme.colors.dark.textSecondary};
    }
  }
`;

const SkipCounter = styled.span`
  color: ${({ theme }) => theme.colors.dark.textMuted};
  font-weight: 400;
`;

const MandatoryMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.accent.red}20;
  border: 1px solid ${({ theme }) => theme.colors.accent.red};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
  
  svg {
    color: ${({ theme }) => theme.colors.accent.red};
    width: 20px;
    height: 20px;
  }
  
  span {
    color: ${({ theme }) => theme.colors.accent.red};
    font-weight: 500;
    font-size: 0.875rem;
  }
`;

const CooldownMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.accent.orange}20;
  border: 1px solid ${({ theme }) => theme.colors.accent.orange};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
  
  svg {
    color: ${({ theme }) => theme.colors.accent.orange};
    width: 20px;
    height: 20px;
  }
  
  span {
    color: ${({ theme }) => theme.colors.accent.orange};
    font-weight: 500;
    font-size: 0.875rem;
    text-align: center;
  }
`;

const SuccessIcon = styled(motion.div)`
  font-size: 4rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const VoiceEnrollment = ({ isExistingUser = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(isExistingUser ? 'existing-prompt' : 'intro');
  const [enrollmentData, setEnrollmentData] = useState({
    samples: [],
    embeddings: null,
    quality: 0
  });
  const [isNavigating, setIsNavigating] = useState(false);
  const [skipStatus, setSkipStatus] = useState(null);
  const [isProcessingSkip, setIsProcessingSkip] = useState(false);

  const userId = user?.uid || user?.id;
  const token = localStorage.getItem('sarvik_token');

  // Initialize skip status
  useEffect(() => {
    if (token) {
      loadSkipStatus();
    }
  }, [token, isExistingUser]);

  const loadSkipStatus = async () => {
    try {
      const status = await voiceSkipService.getSkipStatus(token);
      setSkipStatus(status);
      
      // If in cooldown, show cooldown message and prevent access
      if (status.isInCooldown) {
        setCurrentStep('cooldown');
      } else if (status.isMandatory && !isExistingUser) {
        setCurrentStep('mandatory');
      }
    } catch (error) {
      console.error('Error loading skip status:', error);
    }
  };

  // Check if user wants to skip for 30 days
  useEffect(() => {
    if (!userId) return;
    
    const skipUntil = localStorage.getItem(`voice_skip_until_${userId}`);
    if (skipUntil) {
      const skipDate = new Date(skipUntil);
      if (skipDate > new Date()) {
        // Still within skip period, go to dashboard
        handleNavigateToDashboard();
      }
    }
  }, [userId]);

  const handleNavigateToDashboard = () => {
    setIsNavigating(true);
    setTimeout(() => {
      navigate('/');
      window.location.reload();
    }, 100);
  };

  const handleSkipForNow = async () => {
    if (!token || !skipStatus?.canSkip || isProcessingSkip) return;
    
    setIsProcessingSkip(true);
    
    try {
      const result = await voiceSkipService.processSkip(token);
      
      if (result.success) {
        console.log('Skip processed:', result.message);
        
        // Reload skip status
        await loadSkipStatus();
        
        // Navigate to dashboard
        handleNavigateToDashboard();
      } else {
        console.error('Skip failed:', result.message);
        // Show error message to user
        alert(result.message);
      }
    } catch (error) {
      console.error('Error processing skip:', error);
      alert('Failed to process skip. Please try again.');
    } finally {
      setIsProcessingSkip(false);
    }
  };

  const handleExistingUserChoice = (choice) => {
    if (choice === 'skip') {
      // Just skip for this session - navigate to dashboard
      console.log('Skipping voice setup for this session');
      
      // Mark that we've shown the prompt today
      const currentSessionKey = `voice_prompt_shown_${userId}_${new Date().toDateString()}`;
      sessionStorage.setItem(currentSessionKey, 'true');
      
      handleNavigateToDashboard();
      
    } else if (choice === 'skip30') {
      // Set skip for 30 days
      console.log('Skipping voice setup for 30 days');
      const skipUntil = new Date();
      skipUntil.setDate(skipUntil.getDate() + 30);
      localStorage.setItem(`voice_skip_until_${userId}`, skipUntil.toISOString());
      
      // Mark that we've shown the prompt
      const currentSessionKey = `voice_prompt_shown_${userId}_${new Date().toDateString()}`;
      sessionStorage.setItem(currentSessionKey, 'true');
      
      handleNavigateToDashboard();
      
    } else if (choice === 'update') {
      setCurrentStep('intro');
    }
  };

  const handleStart = () => {
    setCurrentStep('recording');
  };

  const handleRecordingComplete = (samples) => {
    console.log('Recording complete, moving to verification');
    setEnrollmentData(prev => ({ ...prev, samples }));
    setCurrentStep('verification');
  };

  const handleVerificationComplete = async (verified) => {
    if (verified) {
      // Mark voice profile as complete on server
      if (token) {
        try {
          await voiceSkipService.completeVoiceProfile(token);
        } catch (error) {
          console.error('Error completing voice profile on server:', error);
        }
      }
      
      // Mark voice profile as complete locally (for immediate UI update)
      localStorage.setItem(`voice_profile_${userId}`, 'completed');
      localStorage.setItem(`voice_profile_date_${userId}`, new Date().toISOString());
      
      // Show success briefly then redirect
      setCurrentStep('complete');
      
      // Auto-redirect to dashboard after 1.5 seconds
      setTimeout(() => {
        navigate('/');
        // Force a page refresh to ensure proper state update
        window.location.reload();
      }, 1500);
    }
  };

  // Show loading state while navigating
  if (isNavigating) {
    return (
      <EnrollmentContainer>
        <TitleBar>
          <TitleSection>
            <StatusIndicator />
            <AppTitle>SARVIK Assistant - Voice Setup</AppTitle>
          </TitleSection>
          <WindowControls />
        </TitleBar>
        <Content>
          <IntroSection>
            <IntroDescription>Redirecting to dashboard...</IntroDescription>
          </IntroSection>
        </Content>
      </EnrollmentContainer>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 'cooldown':
        return (
          <StepContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <IntroSection>
              <IntroIcon>
                <Clock />
              </IntroIcon>
              <IntroTitle>Please Wait</IntroTitle>
              <CooldownMessage>
                <Clock />
                <span>
                  {skipStatus?.message || 'Please wait before accessing the app'}
                </span>
              </CooldownMessage>
            </IntroSection>
          </StepContainer>
        );

      case 'mandatory':
        return (
          <StepContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <IntroSection>
              <IntroIcon>
                <AlertTriangle />
              </IntroIcon>
              <IntroTitle>Voice Setup Required</IntroTitle>
              <IntroDescription>
                Voice setup is now mandatory for security. You've used all available skips.
              </IntroDescription>
              <MandatoryMessage>
                <AlertTriangle />
                <span>{skipStatus?.message}</span>
              </MandatoryMessage>
              <StartButton
                onClick={handleStart}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Mic />
                Complete Voice Setup
                <ChevronRight />
              </StartButton>
            </IntroSection>
          </StepContainer>
        );

      case 'existing-prompt':
        return (
          <StepContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <ExistingUserPrompt onChoice={handleExistingUserChoice} user={user} />
          </StepContainer>
        );

      case 'intro':
        return (
          <StepContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <IntroSection>
              <IntroIcon>
                <Shield />
              </IntroIcon>
              <IntroTitle>
                {isExistingUser ? 'Update Your Voice Profile' : 'Secure Your Assistant with Your Voice'}
              </IntroTitle>
              <IntroDescription>
                {isExistingUser 
                  ? "Let's update your voice profile for better recognition."
                  : "Voice setup is required for security. SARVIK will learn to recognize your unique voice to keep your data safe."
                }
              </IntroDescription>
              <StartButton
                onClick={handleStart}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Mic />
                {isExistingUser ? 'Update Voice Setup' : 'Start Voice Setup'}
                <ChevronRight />
              </StartButton>
              
              {/* Skip Section - only show for new users and when skips are available */}
              {!isExistingUser && skipStatus && skipStatus.canSkip && (
                <SkipSection>
                  <SkipText>Or</SkipText>
                  <SkipButton
                    onClick={handleSkipForNow}
                    disabled={isProcessingSkip || !skipStatus.canSkip}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isProcessingSkip ? 'Processing...' : 'Skip for now'}
                  </SkipButton>
                  <SkipText>
                    <SkipCounter>
                      ({skipStatus.skipsRemaining} skip{skipStatus.skipsRemaining !== 1 ? 's' : ''} remaining)
                    </SkipCounter>
                  </SkipText>
                  {skipStatus.skipsRemaining === 1 && (
                    <SkipText style={{ color: '#f59e0b', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                      ⚠️ After this skip, voice setup will be mandatory
                    </SkipText>
                  )}
                </SkipSection>
              )}
              
              {/* Show mandatory message if no skips left */}
              {!isExistingUser && skipStatus && skipStatus.isMandatory && (
                <MandatoryMessage>
                  <AlertTriangle />
                  <span>{skipStatus.message}</span>
                </MandatoryMessage>
              )}
            </IntroSection>
          </StepContainer>
        );

      case 'recording':
        return (
          <StepContainer
            key="recording"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
          >
            <VoiceRecorder onComplete={handleRecordingComplete} />
          </StepContainer>
        );

      case 'verification':
        return (
          <StepContainer
            key="verification"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
          >
            <VoiceVerification 
              enrollmentData={enrollmentData}
              onComplete={handleVerificationComplete}
            />
          </StepContainer>
        );

      case 'complete':
        return (
          <StepContainer
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <IntroSection>
              <SuccessIcon
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", duration: 0.6 }}
              >
                ✅
              </SuccessIcon>
              <IntroTitle>Voice Setup Complete!</IntroTitle>
              <IntroDescription>
                Your voice profile has been created successfully. Redirecting to dashboard...
              </IntroDescription>
            </IntroSection>
          </StepContainer>
        );

      default:
        return null;
    }
  };

  return (
    <EnrollmentContainer>
      <TitleBar>
        <TitleSection>
          <StatusIndicator />
          <AppTitle>SARVIK Assistant - Voice Setup</AppTitle>
        </TitleSection>
        <WindowControls />
      </TitleBar>

      <Content>
        {currentStep !== 'existing-prompt' && currentStep !== 'cooldown' && currentStep !== 'mandatory' && (
          <TimelineContainer>
            <EnrollmentSteps currentStep={currentStep} />
          </TimelineContainer>
        )}
        <MainContent>
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>
        </MainContent>
      </Content>
    </EnrollmentContainer>
  );
};

export default VoiceEnrollment;
